import React, { useState, useEffect } from 'react';

function RecordModal({ mode, record, schema, onClose, onSave }) {
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && record) {
      setFormData(record);
    } else {
      // Initialize with empty values
      const initial = {};
      schema.forEach(col => {
        if (!col.isIdentity) {
          initial[col.name] = '';
        }
      });
      setFormData(initial);
    }
  }, [mode, record, schema]);

  const handleChange = (columnName, value) => {
    setFormData(prev => ({
      ...prev,
      [columnName]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      // Convert empty strings to null for nullable fields
      const processedData = {};
      schema.forEach(col => {
        if (formData[col.name] !== undefined && !col.isIdentity) {
          const value = formData[col.name];
          if (value === '' && col.nullable) {
            processedData[col.name] = null;
          } else if (value !== '') {
            processedData[col.name] = convertValue(value, col.type);
          }
        }
      });

      await onSave(processedData);
    } catch (err) {
      setError(err.message || 'Failed to save record');
    } finally {
      setIsSaving(false);
    }
  };

  const convertValue = (value, type) => {
    if (value === null || value === '') return null;

    const lowerType = type.toLowerCase();

    if (lowerType.includes('int') || lowerType.includes('numeric') || lowerType.includes('decimal')) {
      const num = Number(value);
      return isNaN(num) ? value : num;
    }

    if (lowerType.includes('bit')) {
      return value === 'true' || value === '1' || value === true;
    }

    return value;
  };

  const getInputType = (column) => {
    const type = column.type.toLowerCase();

    if (type.includes('int') || type.includes('numeric') || type.includes('decimal') || type.includes('money')) {
      return 'number';
    }

    if (type.includes('date') || type.includes('time')) {
      return type.includes('datetime') ? 'datetime-local' : 'date';
    }

    if (type.includes('bit')) {
      return 'checkbox';
    }

    return 'text';
  };

  const renderInput = (column) => {
    const inputType = getInputType(column);
    const value = formData[column.name] ?? '';

    if (column.isIdentity && mode === 'create') {
      return (
        <input
          type="text"
          disabled
          value="Auto-generated"
          className="input bg-gray-100 cursor-not-allowed"
        />
      );
    }

    if (column.isPrimaryKey && mode === 'edit') {
      return (
        <input
          type="text"
          disabled
          value={value}
          className="input bg-gray-100 cursor-not-allowed"
        />
      );
    }

    if (inputType === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={value === true || value === 1 || value === 'true'}
          onChange={(e) => handleChange(column.name, e.target.checked)}
          className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
        />
      );
    }

    if (column.type.toLowerCase().includes('text') || column.maxLength > 255) {
      return (
        <textarea
          value={value}
          onChange={(e) => handleChange(column.name, e.target.value)}
          className="input"
          rows={3}
          required={!column.nullable && !column.isIdentity}
        />
      );
    }

    return (
      <input
        type={inputType}
        value={value}
        onChange={(e) => handleChange(column.name, e.target.value)}
        className="input"
        required={!column.nullable && !column.isIdentity}
        step={inputType === 'number' && column.type.includes('decimal') ? '0.01' : undefined}
      />
    );
  };

  const editableColumns = schema.filter(col => {
    if (mode === 'create') {
      return !col.isIdentity;
    }
    return !col.isPrimaryKey || !col.isIdentity;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Create New Record' : 'Edit Record'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {editableColumns.map((column) => (
              <div key={column.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {column.name}
                  {column.isPrimaryKey && (
                    <span className="ml-1 text-primary-600" title="Primary Key">🔑</span>
                  )}
                  {!column.nullable && !column.isIdentity && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                  <span className="text-gray-500 font-normal ml-2 text-xs">
                    ({column.type}
                    {column.maxLength && `, max: ${column.maxLength}`}
                    {column.nullable && ', nullable'})
                  </span>
                </label>
                {renderInput(column)}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecordModal;
